<template>
  <!-- Revit rendering with SchemaRevit -->
  <SchemaRevit 
    v-if="isRevitProject && this.floorSchema"
    :floorSchema="floorSchema"
    :floorMapInfo="floorMapInfo"
    :schemaFetching="schemaFetching"
    :activeFloorFlatId="activeFloorFlatId"
    :flatBorders="flatBorders"
    :params="params"
    :floorMapId="floorMapId"
    :activeFloorParamTypeIds="[]"
    :updateParamBody="null"
    :updateData="null"
    :editingMode="false"
    :getFloorSchemaData="null"
    :activeData="null"
    :bufferParams="null"
    :getBufferParamsData="null"
    :getFloorLegendData="null"
    :updateFloorSchema="null"
    :deletingMode="false"
    :activeFlat="null"
    :groupEditingMode="false"
  />
  
  <!-- AutoCAD rendering with Konva -->
  <div
    v-else-if="!isRevitProject && this.floorSchema"
    :style="{...this.wrapperStyle, ...this.wrapperStyles}"
  >
    <v-stage
      :config="{...stageConfig, ...stageView}"
      ref="stage"
      @wheel="onWheel"
    >
      <v-layer>
        <v-image
          v-if="backgroundImage"
          :config="{ image: this.backgroundImage, 
                      x: 0,
                      y: -100, 
                      opacity: 0.4,  
                   }"
        />
        <v-line
          v-for="(wall, index) in this.floorSchema?.lines"
          :key="wall.floor_param_id"
          style="cursor: pointer;"
          :config="{
            points: this.flattenPoints(wall.points),
            stroke: this.getLineColor(wall, `param-${index}`, wall.floor_flat_id), 
            strokeWidth: this.getLineWidth(wall),
            x: 0,
            y: -100, 
            lineJoin: 'round',
            dash: wall?.floor_param_type_id_relative ? [10, 5] : []
        }"
          @click="handleLineClick(wall, $event)"
          @mouseenter="onMouseEnter(wall, $event, `param-${index}`)"
          @mouseleave="onMouseLeave"
        />
        <v-circle
          v-if="!!this.floorSchema?.circles?.length"
          v-for="(circle, index) in this.floorSchema?.circles"
          :config="{
                    x: circle.points[0],
                    y: circle.points[1] - 100,
                    radius: 10,
                    opacity: '1',
                    stroke: this.getLineColor(circle, `circle-${index}`, circle.floor_flat_id),
                    strokeWidth: this.getLineWidth(circle),
                  }"
          @click="handleLineClick(circle, $event)"
          @mouseenter="onMouseEnter(circle, $event, `circle-${index}`)"
          @mouseleave="onMouseLeave"
        />
        <v-circle
          v-if="!!this.floorSchema?.walls_center?.length"
          v-for="room in this.floorSchema?.walls_center"
          :config="this.getCircleSettings(room)"
        />
        <v-text
          v-if="!!this.floorSchema?.walls_center?.length"
          v-for="room in this.floorSchema?.walls_center"
          :key="'label_' + room.flat_num"
          :config="{
              text: `кв ${room.flat_num}`,
              x: room.center_x,
              y: room.center_y - 100,
              fontSize: 40,
              fontStyle: 'bold',
              fontFamily: 'sans-serif',
              fill: '#333',
              align: 'center',
              listening: false
            }"
        />
        <v-text
          v-if="!!this.floorSchema?.area_center?.length"
          v-for="areaCenter in this.floorSchema?.area_center"
          :key="'area_' + areaCenter.room_id"
          :config="{
              text: `${areaCenter.area}`,
              x: areaCenter.center_x - String(areaCenter.area)?.length * 4,
              y: areaCenter.center_y - 12 - 100,
              fontSize: (this.activeFloorFlatId) ? text?.floor_flat_id === this.activeFloorFlatId ? 20 : 0 : 20,
              fontStyle: 'bold',
              fontFamily: 'sans-serif',
              fill: '#333',
              align: 'center',
              listening: false
            }"
        />
        <v-text
          v-if="!!this.floorSchema?.texts?.length"
          v-for="text in this.floorSchema?.texts"
          :key="'frame_' + text.floor_param_id + text.frame_name"
          :config="{
              text: text.frame_name,
              x: text.center_point[0] - text.frame_name.length * 4,
              y: text.center_point[1] - 100,
              fontSize: (this.activeFloorFlatId) ? text?.floor_flat_id === this.activeFloorFlatId ? 12 : 0 : 12,
              fontStyle: 'bold',
              fontFamily: 'sans-serif',
              fill: '#333',
              align: 'center',
              listening: false
            }"
        />
        <v-circle
          v-if="!!this.commentPoints?.length"
          v-for="(circle, index) in this.commentPoints"
          :key="String(circle.call_check_list_point_id)"
          :config="{
                    x: circle.x,
                    y: circle.y - 100,
                    radius: 4,
                    opacity: '1',
                    stroke: this.hoveredIndex === `comment-point-${index}` ? 'blue' : circle.is_accepted ? 'green' : circle?.is_accepted === null ? 'orange' : 'red',
                    fill: this.hoveredIndex === `comment-point-${index}` ? 'blue' : circle.is_accepted ? 'green' : circle?.is_accepted === null ? 'orange' : '#9b0606',
                    strokeWidth: 18,
                  }"
          @click="handlePointCommentClick(circle)"
          @mouseenter="onMouseEnter(circle, $event, `comment-point-${index}`)"
          @mouseleave="onMouseLeave"
        />
      </v-layer>
    </v-stage>
    <q-card
      v-if="!!hoveredWall"
      class="tooltip"
      style="position: absolute; z-index: 9999"
      :style="this.getTooltipStyles()"
    >
      <div>
        {{ this.hoveredIndex?.includes('comment') ? `${this.hoveredWall?.is_accepted ? 'Завершено' : this.hoveredWall?.is_accepted === false ? 'Есть замечания' : 'На проверке'}` : `${hoveredWall?.floor_param_type_name} ${hoveredWall?.floor_param_type_name_relative ? `, ${hoveredWall?.floor_param_type_name_relative}` :''}` }}
      </div>
    </q-card>
  </div>
  <q-linear-progress v-else-if="this.schemaFetching" indeterminate />
  <NotFoundText v-else-if="!this.schemaFetching" />
</template>

<script>
import NotFoundText from '@/components/common/NotFoundText.vue';
import SchemaRevit from './SchemaRevit.vue';
import { MODAL_NAMES, API_URL } from '@/constants';
import { flattenPoints, onKonvaWheel } from './services';
import { mapState } from 'pinia';
import { useAppStore } from '@/services/store/app';

export default {
  name: "Schema",
  components: { NotFoundText, SchemaRevit },
  props: ['floorSchema','schemaFetching','activeFloorFlatId','flatBorders','workSetParams','params','floorMapId','commentPoints','floorMapInfo'],
  data() {
    return {
      stageConfig: {
        draggable: true,
        scaleX: 0.2,
        scaleY: 0.2,
      },
      wrapperStyle: {
        position: 'relative',
      },
      tooltipX: 0,
      tooltipY: 0,
      backgroundImage: null,
      hoveredIndex: null,
      hoveredWall: null,
      activeFloorParamTypeId: null,
      activeObjectId: null,
      objectInfo: null,
    };
  },
  computed: {
    ...mapState(useAppStore, ['menuDrawerOpen']),
    isRevitProject() {
      // Определяем, является ли проект Revit
      const resourceCode = this.floorMapInfo?.external_resource_code
      // Если это не AUTOCAD и есть external_resource_code, значит это Revit
      return resourceCode && resourceCode !== 'AUTOCAD'
    },
    stageView() {
      const view = {
        width: window.innerWidth || 1170,
        height: (window.innerHeight - 230) || 700,
      }
      if(this.menuDrawerOpen)
        view.width = window.innerWidth || 1170
      return view
    },
    wrapperStyles() {
      const view = {
        width: `${window.innerWidth || 1170}px`,
        height: `${(window.innerHeight - 230) || 700}px`,
      }
      if(this.menuDrawerOpen)
        view.width = `${window.innerWidth || 1170}px`
      return view
    },
  },
  methods: {
    onKonvaWheel,
    flattenPoints,
    getTooltipStyles() {
      if(this.menuDrawerOpen)
        return { left: this.tooltipX - 30 + 'px', top: this.tooltipY - 235 + 'px' }
      return { left: this.tooltipX - 280 + 'px', top: this.tooltipY - 235 + 'px' }
    },
    onMouseLeave(evt) {
      this.hoveredWall = null;
      this.hoveredIndex = null;
      const stage = evt.target.getStage();
      stage.container().style.cursor = 'default';
    },
    onMouseEnter(wall, evt, index, tooltip = true) {
      this.hoveredIndex = index
      if(tooltip) {
        this.hoveredWall = wall;
        const pointerPos = evt.evt; // native MouseEvent
        this.tooltipX = pointerPos.clientX + 10;
        this.tooltipY = pointerPos.clientY + 10;
      }

      const stage = evt.target.getStage();
      stage.container().style.cursor = 'pointer';
    },
    loadBackground() {
      const src = this.floorSchema?.floor_map?.image_url
      if(!src) return
      const image = new Image();
      image.src = `${API_URL}${this.floorSchema?.floor_map?.image_url}`; // путь к PNG (можно и base64)
      image.onload = () => {
        this.backgroundImage = image;
      };
    },
    getCircleSettings(room) {
      const {flat_num} = room
      const flatNumLength = String(flat_num).length
      let settings = {
        x: room.center_x + 50,
        y: room.center_y + 17,
        radius: 60,
        listening: false
      }
      if(flatNumLength === 1) {
        settings.x = room.center_x + 40
        settings.radius = 55
      } else if(flatNumLength === 3) {
        settings.x = room.center_x + 61
        settings.radius = 67
      }
      return {
        ...settings,
        x: settings.x,
        y: settings.y + -100,
        fill: '#fff',
        opacity: '.7',
        stroke: '#ccc',
        strokeWidth: 1
      }
    },
    async handleLineClick(wall) {
      return
    },
    async handlePointCommentClick(point) {
      this.showModal(MODAL_NAMES.SchemaPointInfo, {
        title: 'Информация о замечании',
        data: {...point, floorMapId: this.floorMapId},
      })
    },
    getLineWidth(wall) {
      let width = 8
      if(this.activeFloorParamTypeId) {
        if(wall?.floor_param_type_id !== this.activeFloorParamTypeId)
          width = 0
      }
      if(this.activeFloorFlatId) {
        if(wall?.floor_flat_id !== this.activeFloorFlatId)
          width = 0
      }
      return width
    },
    getLineColor(wall, index, floor_flat_id) {
      if(this.hoveredIndex === index) return 'blue'
      if(this.activeObjectId === wall.floor_param_id && !!wall.floor_param_id) return '#970e0e'
      if(this.flatBorders && this.floorSchema?.flatColors) return this.floorSchema?.flatColors[floor_flat_id]

      if(this.workSetParams?.length){
        const isActiveWorkSet = this.workSetParams?.find(item => item.floor_param_id === wall?.floor_param_id)
        if(isActiveWorkSet)
          return '#1976d2'
      }
      return wall?.okk_status_colour || '#000'
    },
    onWheel(e) {
      const stage = this.$refs.stage.getNode();
      this.onKonvaWheel(e, stage)
    },
    setActiveParamTypeId(typeId) {
      if(typeId === this.activeFloorParamTypeId)
        return this.activeFloorParamTypeId = null
      this.activeFloorParamTypeId = typeId
    },
  },
  watch: {
    floorSchema: function () {
      this.loadBackground()
    },
  },
  mounted() {
    this.loadBackground();
  },
  setup() {
    const {showModal} = useAppStore()
    return {showModal}
  },
};
</script>

<style scoped lang="scss">
.tooltip {
  background-color: rgba(0, 0, 0, 0.6);
  color: #fff;
  padding: 5px;
  box-shadow: none;
  & div {
    font-size: 11px;
    line-height: 1;
  }
}
</style>
